import type { User } from "firebase/auth";
import { isFirebaseConfigured } from "@/auth/firebase";
import { callGetLeaderboard } from "@/cloud/game-callables";
import {
  LEADERBOARD_BOARD_LABELS,
  type LeaderboardBoard,
} from "@/rank/leaderboard-types";

const BOARDS: LeaderboardBoard[] = ["raid", "tower", "wealth"];

export type MountRankPageOptions = {
  asModal?: boolean;
  onRequestClose?: () => void;
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function rankRowClass(rank: number): string {
  if (rank === 1) return "rank-row--top rank-row--1";
  if (rank === 2) return "rank-row--top rank-row--2";
  if (rank === 3) return "rank-row--top rank-row--3";
  return "";
}

export async function mountRankPage(
  root: HTMLElement,
  _user: User,
  options?: MountRankPageOptions,
): Promise<() => void> {
  const asModal = options?.asModal === true;
  if (asModal) {
    root.className = "rank-modal-host";
  } else {
    root.className = "page-shell page-rank player-game-ui";
  }
  let active: LeaderboardBoard = "raid";
  let status = "";

  const loadBoard = async (board: LeaderboardBoard) => {
    if (!isFirebaseConfigured()) {
      status = "ต้องตั้งค่า Firebase เพื่อดูอันดับ";
      return { rows: [], me: undefined };
    }
    try {
      return await callGetLeaderboard(board);
    } catch (err) {
      status = err instanceof Error ? err.message : "โหลดอันดับไม่สำเร็จ";
      return { rows: [], me: undefined };
    }
  };

  const render = async () => {
    const tabs = BOARDS
      .map((b) => {
        const activeCls = b === active ? " rank-board-tab--active" : "";
        return `<button type="button" class="rank-board-tab${activeCls}" data-rank-tab="${b}">${LEADERBOARD_BOARD_LABELS[b]}</button>`;
      })
      .join("");

    const closeBtn = asModal
      ? `<button type="button" class="dungeon-frame-close" id="rank-page-close" aria-label="ปิด">×</button>`
      : "";

    const footNav = asModal
      ? ""
      : `<footer class="rank-page-foot"><a class="dungeon-foot-link" href="/home?dungeon=1">← ด่าน</a></footer>`;

    root.innerHTML = `
      <main class="dungeon-lobby dungeon-lobby--game rank-lobby">
        <div class="dungeon-frame game-system-frame rank-frame">
          <header class="dungeon-frame-head">
            <div class="dungeon-frame-title-wrap">
              <p class="dungeon-frame-kicker">อันดับโลก</p>
              <h1 class="dungeon-frame-title">Leaderboard</h1>
            </div>
            ${closeBtn}
          </header>
          <p class="dungeon-frame-lede">ป้ายแรงค์ + บัฟ · คำนวณทุกจันทร์ 00:00 UTC</p>
          <p class="rank-page-status" role="status">${escapeHtml(status)}</p>
          <nav class="rank-board-tabs" aria-label="เลือกบอร์ด">${tabs}</nav>
          <section class="rank-table-panel" aria-labelledby="rank-table-title">
            <h2 id="rank-table-title" class="dungeon-section-title">100 อันดับแรก</h2>
            <div id="rank-table" class="rank-table"><p class="rank-table-loading">กำลังโหลด…</p></div>
          </section>
        </div>
        ${footNav}
      </main>
    `;

    root.querySelectorAll<HTMLButtonElement>("[data-rank-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        active = btn.dataset.rankTab as LeaderboardBoard;
        void render();
      });
    });

    root.querySelector("#rank-page-close")?.addEventListener("click", () => {
      options?.onRequestClose?.();
    });

    const data = await loadBoard(active);
    const table = root.querySelector<HTMLElement>("#rank-table");
    if (!table) return;
    const rows = data.rows
      .slice(0, 100)
      .map((r) => {
        const medal =
          r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : String(r.rank);
        const rankCell =
          r.rank <= 3
            ? `<span class="rank-medal" aria-label="อันดับ ${r.rank}">${medal}</span>`
            : escapeHtml(String(r.rank));
        return `<tr class="${rankRowClass(r.rank)}"><td class="rank-col-rank">${rankCell}</td><td class="rank-col-name">${escapeHtml(r.displayName)}</td><td class="rank-col-score">${r.value.toLocaleString("en-US")}</td></tr>`;
      })
      .join("");
    const meRow = data.me
      ? `<div class="rank-me-chip" role="status">
          <span class="rank-me-label">คุณ</span>
          <strong>#${data.me.rank > 100 ? "100+" : data.me.rank}</strong>
          <span class="rank-me-score">${data.me.value.toLocaleString("en-US")}</span>
        </div>`
      : "";
    table.innerHTML = `
      ${meRow}
      <table class="rank-leaderboard-table">
        <thead><tr><th>#</th><th>ชื่อ</th><th>คะแนน</th></tr></thead>
        <tbody>${rows || "<tr><td colspan='3' class='rank-empty'>ยังไม่มีข้อมูล</td></tr>"}</tbody>
      </table>
    `;
  };

  await render();
  return () => {};
}
